import React, { useMemo } from 'react';
import { Box, HStack, Select, SelectOptionProps, TextInput, TimePicker, Text, Link } from '@televet/kibble-ui';
import { Link as ReactRouterLink } from 'react-router-dom';
import { AutomatedTriggerType } from 'shared/types/graphql';
import {
  AutomationsFieldDescription,
  AutomationsFieldLabel,
  AutomationsFieldContainer,
} from 'pages/Automations/components/Layout';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { updateAutomationSendTiming } from '../../../state/automationsSlice';
import { TimeIncrement } from '../../../types/AutomationDraft';
import { WorkflowEvent } from 'shared/types/graphql';
import { formatPluralization } from 'shared/utils';
import { set } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { RouteBasePaths, SettingsRoutes } from 'routes';
import { getTimezoneAbbreviation, getTimezoneDisplayName } from 'shared/utils/timezoneDisplayNames';
import useClinicUser from 'shared/hooks/useClinicUser';

export const SendTiming = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const sendTiming = useAppSelector((state) => state.automations.automationDraft.sendTiming);
  const automationType = useAppSelector((state) => state.automations.automationDraft.automationType);
  const { currentClinic } = useClinicUser();
  const clinicTimezoneName = currentClinic?.timezoneName || '';

  const handleTimeValueChange: React.ChangeEventHandler<HTMLInputElement> = (event): void => {
    const parsed = parseInt(event.target.value);
    const timeValue = isNaN(parsed) ? null : parsed;
    dispatch(updateAutomationSendTiming({ timeValue: timeValue }));
  };

  const handleTimeIncrementChange = (option: SelectOptionProps): void => {
    dispatch(updateAutomationSendTiming({ timeIncrement: option.value as TimeIncrement, timeOfDay: null }));
  };

  const handleTriggerTypeChange = (option: SelectOptionProps): void => {
    dispatch(updateAutomationSendTiming({ triggerType: option.value as AutomatedTriggerType }));
  };

  const handleTimeOfDayChange = (time: Date): void => {
    dispatch(updateAutomationSendTiming({ timeOfDay: zonedTimeToUtc(time, clinicTimezoneName).toISOString() }));
  };

  const isBeforeEnabled = useMemo(() => {
    switch (automationType) {
      case WorkflowEvent.AppointmentReminder:
      case WorkflowEvent.AppointmentConfirmReminder:
      case WorkflowEvent.AppointmentClientArrival:
        return true;
      default:
        return false;
    }
  }, [automationType]);

  const isAfterEnabled = useMemo(() => {
    switch (automationType) {
      case WorkflowEvent.AppointmentFollowup:
        return true;
      default:
        return false;
    }
  }, [automationType]);

  const pluralizedTimeIncrements = useMemo(() => {
    const defaultTimeIncrements = {
      days: 'Day',
      hours: 'Hour',
      minutes: 'Minute',
    };

    if (!sendTiming.timeValue || sendTiming.timeValue === 1) return defaultTimeIncrements;

    return {
      days: formatPluralization({ word: 'Day', singularCriteria: sendTiming.timeValue }),
      hours: formatPluralization({ word: 'Hour', singularCriteria: sendTiming.timeValue }),
      minutes: formatPluralization({ word: 'Minute', singularCriteria: sendTiming.timeValue }),
    };
  }, [sendTiming]);

  const timeOfDay = useMemo(() => {
    if (sendTiming.timeOfDay) {
      return utcToZonedTime(sendTiming.timeOfDay, clinicTimezoneName);
    }
    return utcToZonedTime(set(new Date(), { hours: 9, minutes: 0 }), clinicTimezoneName);
  }, [clinicTimezoneName, sendTiming.timeOfDay]);

  const timeOfDayKey = useMemo(() => {
    return timeOfDay.toISOString();
  }, [timeOfDay]);

  return (
    <AutomationsFieldContainer>
      <AutomationsFieldLabel>Send Timing</AutomationsFieldLabel>
      <AutomationsFieldDescription>
        Based on the appointment date and time, this automation will send at the following time. Times are in{' '}
        {getTimezoneDisplayName(clinicTimezoneName)} and can be changed in{' '}
        <Link as={ReactRouterLink} to={`${RouteBasePaths.Settings}${SettingsRoutes.ClinicHours}`}>
          Clinic Hours
        </Link>
        .
      </AutomationsFieldDescription>
      <HStack mb={2} justifyContent="flex-start" className="Automations__SendTiming-Inputs">
        <Box w="76px">
          <TextInput type="number" value={sendTiming.timeValue || undefined} onChange={handleTimeValueChange} />
        </Box>
        <Box w="108px">
          <Select
            value={sendTiming.timeIncrement || TimeIncrement.Hours}
            options={[
              { label: pluralizedTimeIncrements?.days, value: TimeIncrement.Days },
              { label: pluralizedTimeIncrements?.hours, value: TimeIncrement.Hours },
              { label: pluralizedTimeIncrements?.minutes, value: TimeIncrement.Minutes },
            ]}
            listProps={{ onSelect: handleTimeIncrementChange }}
          />
        </Box>
        <Box w="108px">
          <Select
            value={sendTiming.triggerType || AutomatedTriggerType.Before}
            options={[
              ...(isBeforeEnabled ? [{ label: 'Before', value: AutomatedTriggerType.Before }] : []),
              ...(isAfterEnabled ? [{ label: 'After', value: AutomatedTriggerType.After }] : []),
            ]}
            listProps={{ onSelect: handleTriggerTypeChange }}
          />
        </Box>
        {(sendTiming.timeIncrement === TimeIncrement.Days || !!sendTiming.timeOfDay) && (
          <HStack position="relative">
            <Text>at </Text>
            <Box maxW="140px" position="relative">
              <TimePicker
                // Keying on time of day string ensures the state of the TimePicker (an uncontrolled component, for now)
                // resets with the current automation's persisted value
                key={timeOfDayKey}
                placement="bottom-start"
                initialTime={timeOfDay}
                inputProps={{ rightElement: undefined, w: '104px' }}
                onTimeChange={handleTimeOfDayChange}
              />
            </Box>
            <Text>{getTimezoneAbbreviation(clinicTimezoneName)} </Text>
          </HStack>
        )}
      </HStack>
    </AutomationsFieldContainer>
  );
};
